import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import styled from 'styled-components';

import { FieldContainerIw } from '@/_pages/interwill/components/FieldContainerIw';
import { SelectVfoTag } from '@/entities/vfo/components/SelectVfoTag';
import { useNotices } from '@/features/notices/useNotices';
import { generateInvitationLink } from '@/features/vfo/helpers';
import { useMembers } from '@/features/vfo/hooks/useMembers';
import { Form, FormItem } from '@/features/vfo/uikit/Form';
import { useApi } from '@/shared/api';
import { ERROR_CODES } from '@/shared/api/errorCodes';
import { Input } from '@/shared/components';
import { useSJDatabase } from '@/shared/lib/sj-orm';
import { useAuthStore } from '@/shared/store/auth.store';
import { useProfileStore } from '@/shared/store/profile.store';
import { Button } from '@/shared/uikitV2/Button';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { InviteMemberModalProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../../interwill/containers/ModalContainerV1';

import { InviteMemberFormData, validationSchema } from './validation';

export const InviteMemberModal: FC<InviteMemberModalProps> = () => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'inviteMember' });
  const modals = useModalsStore();
  const api = useApi();
  const sjDb = useSJDatabase();
  const { account } = useAuthStore();
  const notices = useNotices();

  const { fetchMembers, members } = useMembers();
  const { firstName, lastName } = useProfileStore();

  const {
    control,
    handleSubmit: formHandleSubmit,
    formState: { isSubmitting },
    setError,
  } = useForm<InviteMemberFormData>({
    resolver: yupResolver(validationSchema),
    context: { members },
  });

  const onSubmit = async (values: InviteMemberFormData) => {
    const tenantProfileId = account?.id;
    if (!tenantProfileId) return;

    const beneficiary = sjDb.collections.beneficiaries?.findOne((b) => b.email === values.email);
    let invitationLink;

    if (beneficiary) {
      const { error } = await api.scopeCreate({
        scopeType: 'VFO',
        tag: values.tag,
        profileId: beneficiary.centralDbProfileId,
      });

      if (error) {
        notices.error(t('errors.generic'));
        return;
      }
    } else {
      const { data: invitationData, error: invitationError } = await api.vfoInviteMember({
        email: values.email,
        tag: values.tag,
        firstName: values.firstName,
        lastName: values.lastName,
      });

      if (invitationError?.errorCode === ERROR_CODES.HAS_SCOPE_ERROR_CODE) {
        setError('email', { message: t('errors.emailExists') });
        return;
      }

      if (!invitationData || !firstName || !lastName) {
        notices.error(t('errors.generic'));
        return;
      }

      invitationLink = generateInvitationLink({
        token: invitationData.invitationToken ?? '',
        email: invitationData.email,
        tag: values.tag,
        tenantProfileFirstName: firstName,
        tenantProfileLastName: lastName,
        firstName: invitationData.firstName ?? '',
        lastName: invitationData.lastName ?? '',
      });
    }

    await fetchMembers();
    modals.closeModal();
    modals.openModal({
      type: 'InvitationSuccessModal',
      invitationLink,
      sentEmail: values.email,
    });
  };

  return (
    <ModalContainerV1 title={t('title')}>
      <Form onFinish={formHandleSubmit(onSubmit)}>
        <FormItems>
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState }) => (
              <FieldContainerIw label={t('fields.email.label')} required>
                <FormItem
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={
                    fieldState.error?.message ? t(`errors.${fieldState.error?.message}`) : undefined
                  }
                >
                  <Input placeholder={t('fields.email.placeholder')} type="text" {...field} />
                </FormItem>
              </FieldContainerIw>
            )}
          />
          <Controller
            name="tag"
            control={control}
            render={({ field, fieldState }) => (
              <FieldContainerIw label={t('fields.tag.label')} required>
                <FormItem
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={
                    fieldState.error?.message ? t(`errors.${fieldState.error?.message}`) : undefined
                  }
                >
                  <SelectVfoTag placeholder={t('fields.tag.placeholder')} {...field} />
                </FormItem>
              </FieldContainerIw>
            )}
          />
          <Row>
            <Controller
              name="firstName"
              control={control}
              render={({ field, fieldState }) => (
                <FieldContainerIw label={t('fields.firstName.label')}>
                  <FormItem
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input placeholder={t('fields.firstName.placeholder')} type="text" {...field} />
                  </FormItem>
                </FieldContainerIw>
              )}
            />
            <Controller
              name="lastName"
              control={control}
              render={({ field, fieldState }) => (
                <FieldContainerIw label={t('fields.lastName.label')}>
                  <FormItem
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input placeholder={t('fields.lastName.placeholder')} type="text" {...field} />
                  </FormItem>
                </FieldContainerIw>
              )}
            />
          </Row>
        </FormItems>
        <Actions>
          <Button loading={isSubmitting} size="middle" htmlType="submit">
            {t('sendBtn')}
          </Button>
        </Actions>
      </Form>
    </ModalContainerV1>
  );
};

const FormItems = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Row = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const Actions = styled.div`
  padding: 16px 24px;
  display: flex;
  justify-content: end;
  align-items: center;
`;
